import React from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Sheet, SheetContent } from "@core/components/ui/sheet";
import PrePublishHeader from "./PrePublishHeader";
import SettingGroup from "./SettingGroup";
import SettingRow from "./SettingRow";
import SettingEditor from "./SettingEditor";
import type { DraftSummary, SettingGroupKey, SettingKey } from "./types";
import type { Hint } from "./useSettingsHints";

interface PrePublishModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  draft: DraftSummary;
  onPublish: () => void;
  onCancel: () => void;
  /** Hints actifs (PR 2) — index par setting key */
  hintsByKey?: Partial<Record<SettingKey, Hint>>;
  /** Click "Utiliser « X » →" → applique la valeur suggérée */
  onApplySuggestion?: (key: SettingKey, value: unknown) => void;
  /** Commit du SettingEditor — patche le state parent */
  onEdit?: (key: SettingKey, value: unknown) => void;
}

const useIsMobile = (): boolean => {
  const [isMobile, setIsMobile] = React.useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.matchMedia("(max-width: 639px)").matches;
  });
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 639px)");
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

/** Composant interne — corps du modal (mêmes 3 groupes desktop/mobile) */
const ModalBody: React.FC<{
  draft: DraftSummary;
  hintsByKey?: PrePublishModalProps["hintsByKey"];
  onApplySuggestion?: PrePublishModalProps["onApplySuggestion"];
  onEdit?: PrePublishModalProps["onEdit"];
  expandedKey: SettingKey | null;
  onExpandRequest: (key: SettingKey | null) => void;
  compact: boolean;
}> = ({
  draft,
  hintsByKey,
  onApplySuggestion,
  onEdit,
  expandedKey,
  onExpandRequest,
  compact,
}) => {
  const { t } = useTranslation("polls");
  const byGroup = React.useMemo(() => {
    const acc: Record<SettingGroupKey, typeof draft.settings> = {
      important: [],
      hint: [],
      advanced: [],
    };
    for (const s of draft.settings) acc[s.group].push(s);
    return acc;
  }, [draft.settings]);

  const hintCount = React.useMemo(() => {
    if (!hintsByKey) return 0;
    // Compte les hints actifs dont la key correspond à un slot du groupe "hint"
    return byGroup.hint.filter((s) => hintsByKey[s.key]).length;
  }, [hintsByKey, byGroup.hint]);

  return (
    <div className="space-y-5">
      <PrePublishHeader
        title={draft.title}
        selectedDatesCount={draft.selectedDatesCount}
        timeSlotsCount={draft.timeSlotsCount}
        participantEmailsCount={draft.participantEmailsCount}
        compact={compact}
      />

      {byGroup.important.length > 0 && (
        <SettingGroup
          title={t("prePublish.group.important")}
          tone="neutral"
          testid="setting-group-important"
        >
          {byGroup.important.map((s) => (
            <SettingRow
              key={s.key}
              setting={s}
              onEditRequest={(k) =>
                onExpandRequest(expandedKey === k ? null : k)
              }
              expanded={expandedKey === s.key}
              hint={hintsByKey?.[s.key] ?? null}
              onApplySuggestion={onApplySuggestion}
              editor={
                expandedKey === s.key && onEdit ? (
                  <SettingEditor
                    setting={s}
                    onCommit={(k, v) => {
                      onEdit(k, v);
                      onExpandRequest(null);
                    }}
                    onCancel={() => onExpandRequest(null)}
                  />
                ) : undefined
              }
            />
          ))}
        </SettingGroup>
      )}

      {byGroup.hint.length > 0 && (
        <SettingGroup
          title={t("prePublish.group.suggestions")}
          tone="warn"
          hintBadge={hintCount}
          testid="setting-group-hint"
        >
          {byGroup.hint.map((s) => (
            <SettingRow
              key={s.key}
              setting={s}
              onEditRequest={(k) =>
                onExpandRequest(expandedKey === k ? null : k)
              }
              expanded={expandedKey === s.key}
              hint={hintsByKey?.[s.key] ?? null}
              onApplySuggestion={onApplySuggestion}
              editor={
                expandedKey === s.key && onEdit ? (
                  <SettingEditor
                    setting={s}
                    onCommit={(k, v) => {
                      onEdit(k, v);
                      onExpandRequest(null);
                    }}
                    onCancel={() => onExpandRequest(null)}
                  />
                ) : undefined
              }
            />
          ))}
        </SettingGroup>
      )}

      {byGroup.advanced.length > 0 && (
        <SettingGroup
          title={t("prePublish.group.advanced")}
          tone="muted"
          collapsible
          defaultCollapsed
          testid="setting-group-advanced"
        >
          {byGroup.advanced.map((s) => (
            <SettingRow
              key={s.key}
              setting={s}
              onEditRequest={(k) =>
                onExpandRequest(expandedKey === k ? null : k)
              }
              expanded={expandedKey === s.key}
              hint={hintsByKey?.[s.key] ?? null}
              onApplySuggestion={onApplySuggestion}
              editor={
                expandedKey === s.key && onEdit ? (
                  <SettingEditor
                    setting={s}
                    onCommit={(k, v) => {
                      onEdit(k, v);
                      onExpandRequest(null);
                    }}
                    onCancel={() => onExpandRequest(null)}
                  />
                ) : undefined
              }
            />
          ))}
        </SettingGroup>
      )}
    </div>
  );
};

/**
 * Modal/bottom-sheet de récapitulatif avant publication. Pattern B du
 * handoff Settings Surfacing. Rend `<Dialog>` (desktop) ou `<Sheet
 * side="bottom">` (mobile, breakpoint sm). En PR 1 les rows sont read-only ;
 * la prop `onEditRequest` sera câblée à un `<SettingEditor>` inline en PR 3.
 */
const PrePublishModal: React.FC<PrePublishModalProps> = ({
  open,
  onOpenChange,
  draft,
  onPublish,
  onCancel,
  hintsByKey,
  onApplySuggestion,
  onEdit,
}) => {
  const { t } = useTranslation("polls");
  const isMobile = useIsMobile();
  const [expandedKey, setExpandedKey] = React.useState<SettingKey | null>(null);
  // Refermer toute édition quand le modal se ferme
  React.useEffect(() => {
    if (!open) setExpandedKey(null);
  }, [open]);

  const handleCancel = () => {
    onCancel();
    onOpenChange(false);
  };
  const handlePublish = () => {
    onPublish();
    onOpenChange(false);
  };

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent
          side="bottom"
          data-testid="pre-publish-modal"
          data-variant="mobile"
          className="p-0 flex flex-col gap-0 max-h-[calc(100vh-36px)] h-[76vh] rounded-t-[18px] border-t border-border bg-card"
        >
          <div
            className="mx-auto mt-2 h-1 w-9 rounded-full bg-muted-foreground/30"
            aria-hidden
          />
          <div className="flex-1 overflow-y-auto px-4 py-4">
            <ModalBody
              draft={draft}
              hintsByKey={hintsByKey}
              onApplySuggestion={onApplySuggestion}
              onEdit={onEdit}
              expandedKey={expandedKey}
              onExpandRequest={setExpandedKey}
              compact
            />
          </div>
          <div className="border-t border-border bg-card px-4 pt-3 pb-5 flex flex-col gap-2">
            <button
              type="button"
              data-testid="pre-publish-publish-button"
              onClick={handlePublish}
              className="w-full h-[46px] rounded-[11px] bg-primary text-primary-foreground font-semibold text-[14px] hover:bg-primary/90 transition-colors"
            >
              {t("prePublish.action.publishNow")}
            </button>
            <button
              type="button"
              data-testid="pre-publish-cancel-button"
              onClick={handleCancel}
              className="w-full h-[34px] bg-transparent text-muted-foreground text-[12px] hover:text-foreground transition-colors"
            >
              {t("prePublish.action.backToForm")}
            </button>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/55 backdrop-blur-[4px] data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          data-testid="pre-publish-modal"
          data-variant="desktop"
          className="fixed left-[50%] top-[50%] z-50 w-[620px] max-w-[calc(100vw-32px)] max-h-[calc(100vh-70px)] translate-x-[-50%] translate-y-[-50%] rounded-[16px] border border-border bg-card text-foreground shadow-[0_30px_80px_rgba(0,0,0,0.6)] flex flex-col overflow-hidden data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0"
        >
          <DialogPrimitive.Close
            data-testid="pre-publish-close"
            className="absolute right-4 top-4 z-10 rounded-md p-1 text-muted-foreground hover:text-foreground transition-colors"
            aria-label={t("prePublish.action.close")}
          >
            <X className="h-4 w-4" />
          </DialogPrimitive.Close>

          <div className="flex-1 overflow-y-auto px-5 pt-5 pb-4">
            <ModalBody
              draft={draft}
              hintsByKey={hintsByKey}
              onApplySuggestion={onApplySuggestion}
              onEdit={onEdit}
              expandedKey={expandedKey}
              onExpandRequest={setExpandedKey}
              compact={false}
            />
          </div>

          <div className="border-t border-border bg-card/50 px-5 py-3 flex items-center gap-2.5">
            <button
              type="button"
              data-testid="pre-publish-cancel-button"
              onClick={handleCancel}
              className="h-10 px-4 rounded-[9px] border border-border bg-transparent text-muted-foreground text-[13px] hover:bg-muted/50 transition-colors"
            >
              ← {t("prePublish.action.backToForm")}
            </button>
            <div className="flex-1" />
            <span className="text-[11px] text-muted-foreground/40 mr-1.5">
              {t("prePublish.readyToPublish")}
            </span>
            <button
              type="button"
              data-testid="pre-publish-publish-button"
              onClick={handlePublish}
              className="h-10 px-5 rounded-[9px] bg-primary text-primary-foreground text-[13px] font-semibold hover:bg-primary/90 transition-colors"
            >
              {t("prePublish.action.publishNow")}
            </button>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
};

export default PrePublishModal;
